<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <h2 class="mt-6 text-3xl font-bold text-gray-900">
          認証コード入力
        </h2>
        <p class="mt-2 text-sm text-gray-600">
          {{ email }} に送信された6桁の認証コードを入力してください
        </p>
      </div>

      <!-- OTP Verify Form -->
      <form class="mt-8 space-y-6" @submit.prevent="handleSubmit">
        <div class="space-y-4">
          <!-- OTP Code Input -->
          <div>
            <label for="otp_code" class="form-label">認証コード</label>
            <input
              id="otp_code"
              ref="otpInput"
              v-model="otpCode"
              type="text"
              inputmode="numeric"
              pattern="[0-9]{6}"
              maxlength="6"
              class="form-input block w-full text-center text-2xl tracking-widest"
              :class="{ 'border-danger-300': otpError }"
              placeholder="123456"
              :disabled="loading"
              autocomplete="one-time-code"
              required
              @input="handleOtpInput"
              @blur="validateOtp"
            />
            <p v-if="otpError" class="form-error">
              {{ otpError }}
            </p>
          </div>

          <!-- Resend OTP -->
          <div class="text-center">
            <button
              type="button"
              class="text-sm text-primary-600 hover:text-primary-500 disabled:opacity-50"
              :disabled="resendCooldown > 0 || loading"
              @click="handleResendOtp"
            >
              <template v-if="resendCooldown > 0">
                再送信可能まで {{ resendCooldown }} 秒
              </template>
              <template v-else>
                認証コードを再送信
              </template>
            </button>
          </div>

          <!-- Info Box -->
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-yellow-800">
                  認証コードが届かない場合
                </h3>
                <div class="mt-2 text-sm text-yellow-700">
                  <ul class="list-disc list-inside">
                    <li>迷惑メールフォルダをご確認ください</li>
                    <li>メールアドレスが正しいか確認してください</li>
                    <li>数分待ってから再送信をお試しください</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div>
          <AppButton
            type="submit"
            variant="primary"
            size="lg"
            :loading="loading"
            :disabled="!isFormValid || loading"
            full-width
          >
            認証する
          </AppButton>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="alert-danger">
          {{ errorMessage }}
        </div>
      </form>

      <!-- Footer Links -->
      <div class="text-center text-sm text-gray-600">
        <router-link
          to="/auth/login"
          class="font-medium text-primary-600 hover:text-primary-500"
        >
          ← メールアドレス入力に戻る
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '../../stores/auth.js'
import AppButton from '../../components/ui/AppButton.vue'

const router = useRouter()
const route = useRoute()
const toast = useToast()
const authStore = useAuthStore()

const email = ref(route.query.email || '')
const otpCode = ref('')
const otpError = ref('')
const errorMessage = ref('')
const loading = ref(false)
const resendCooldown = ref(0)
const otpInput = ref(null)

let cooldownInterval = null

const isFormValid = computed(() => {
  return otpCode.value.length === 6 && !otpError.value
})

const validateOtp = () => {
  if (!otpCode.value) {
    otpError.value = '認証コードを入力してください'
    return false
  }

  if (otpCode.value.length !== 6) {
    otpError.value = '6桁の認証コードを入力してください'
    return false
  }

  if (!/^\d{6}$/.test(otpCode.value)) {
    otpError.value = '認証コードは数字のみで入力してください'
    return false
  }

  otpError.value = ''
  return true
}

const handleOtpInput = (event) => {
  // 数字のみ許可
  otpCode.value = event.target.value.replace(/\D/g, '').slice(0, 6)

  // 自動的にバリデーション
  if (otpCode.value.length === 6) {
    validateOtp()
  } else {
    otpError.value = ''
  }
}

const handleSubmit = async () => {
  if (!validateOtp()) {
    return
  }

  loading.value = true
  errorMessage.value = ''

  try {
    await authStore.verifyOtp({
      email: email.value,
      code: otpCode.value
    })

    toast.success('ログインしました')

    // リダイレクト先に遷移
    const redirectPath = route.query.redirect || '/dashboard'
    router.push(redirectPath)
  } catch (error) {
    console.error('OTP Verify error:', error)
    errorMessage.value = error.message || 'OTP認証に失敗しました'
    toast.error(errorMessage.value)

    // エラー時はOTPコードをクリア
    otpCode.value = ''
    otpInput.value?.focus()
  } finally {
    loading.value = false
  }
}

const handleResendOtp = async () => {
  if (resendCooldown.value > 0) return

  loading.value = true
  errorMessage.value = ''

  try {
    await authStore.requestOtp({ email: email.value })

    toast.success('認証コードを再送信しました')

    // クールダウン開始（60秒）
    startCooldown(60)

    // OTPコードをクリア
    otpCode.value = ''
    otpError.value = ''
    otpInput.value?.focus()
  } catch (error) {
    console.error('OTP Resend error:', error)
    errorMessage.value = error.message || 'OTP再送信に失敗しました'
    toast.error(errorMessage.value)
  } finally {
    loading.value = false
  }
}

const startCooldown = (seconds) => {
  resendCooldown.value = seconds

  cooldownInterval = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0) {
      clearInterval(cooldownInterval)
    }
  }, 1000)
}

onMounted(() => {
  // メールアドレスがない場合はログインページにリダイレクト
  if (!email.value) {
    toast.error('メールアドレスが指定されていません')
    router.push('/auth/login')
    return
  }

  // 既にログイン済みの場合はダッシュボードにリダイレクト
  if (authStore.isAuthenticated) {
    router.push('/dashboard')
    return
  }

  // OTP入力にフォーカス
  setTimeout(() => {
    otpInput.value?.focus()
  }, 100)

  // 初回クールダウン開始（60秒）
  startCooldown(60)
})

onUnmounted(() => {
  if (cooldownInterval) {
    clearInterval(cooldownInterval)
  }
})
</script>
